import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronRight, Tag, X, Home, Shield, Phone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSettings } from "@/hooks/useSettings";
import defaultLogo from "@/assets/rup-shoili-logo.jpg";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface CategoryRow {
  id: string;
  name: string;
  slug: string | null;
  image_url: string | null;
  parent_id: string | null;
  display_order: number;
}

interface CategoryNode extends CategoryRow {
  children: CategoryNode[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CategoryDrawer = ({ open, onOpenChange }: Props) => {
  const settings = useSettings();
  const [params] = useSearchParams();
  const activeCat = params.get("cat")?.toLowerCase() ?? null;
  const [tree, setTree] = useState<CategoryNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("categories")
        .select("id, name, slug, image_url, parent_id, display_order")
        .order("display_order", { ascending: true })
        .order("name");
      const rows = (data ?? []) as CategoryRow[];
      const map = new Map<string, CategoryNode>();
      rows.forEach((r) => map.set(r.id, { ...r, children: [] }));
      const roots: CategoryNode[] = [];
      rows.forEach((r) => {
        const node = map.get(r.id)!;
        if (r.parent_id && map.has(r.parent_id)) {
          map.get(r.parent_id)!.children.push(node);
        } else {
          roots.push(node);
        }
      });
      setTree(roots);
      setLoading(false);
    };
    load();
  }, [open]);

  const toggle = (id: string) =>
    setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const isActive = (c: CategoryNode) =>
    !!activeCat &&
    ((c.slug ?? "").toLowerCase() === activeCat ||
      c.name.toLowerCase() === activeCat ||
      c.id.toLowerCase() === activeCat);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-[80%] sm:w-[60%] md:max-w-[340px] p-0 flex flex-col gap-0 border-r-0 bg-background [&>button.absolute]:hidden"
      >
        {/* Premium gradient header */}
        <SheetHeader className="relative px-6 pt-6 pb-5 space-y-0 bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground overflow-hidden">
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary-foreground/10 blur-2xl" />
          <div className="absolute -left-4 -bottom-10 h-24 w-24 rounded-full bg-primary-foreground/10 blur-2xl" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary-foreground/15 backdrop-blur-sm overflow-hidden ring-1 ring-primary-foreground/20">
                <img
                  src={settings.logo_url || defaultLogo}
                  alt={`${settings.store_name || "Rupshoili"} logo`}
                  className="h-full w-full object-cover"
                />
              </div>
              <div>
                <SheetTitle className="text-primary-foreground text-base font-semibold leading-tight">
                  {settings.store_name || "Browse Categories"}
                </SheetTitle>
                <p className="text-[11px] text-primary-foreground/75 leading-tight mt-0.5">
                  Find what you love
                </p>
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="grid h-8 w-8 place-items-center rounded-full bg-primary-foreground/15 hover:bg-primary-foreground/25 transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </SheetHeader>

        <nav className="flex-1 overflow-y-auto px-2 py-3 scrollbar-none">
          {/* Static menu links */}
          <ul className="space-y-0.5 mb-2">
            {[
              { to: "/", label: "Home", Icon: Home },
              { to: "/privacy-policy", label: "Privacy Policy", Icon: Shield },
              { to: "/contact", label: "Contact", Icon: Phone },
            ].map(({ to, label, Icon }) => (
              <li key={to}>
                <Link
                  to={to}
                  onClick={() => onOpenChange(false)}
                  className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-foreground/85 hover:bg-muted/70 hover:text-primary transition-all duration-200"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-all">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="truncate">{label}</span>
                </Link>
              </li>
            ))}
          </ul>

          <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Categories
          </div>
          {loading ? (
            <div className="px-2 space-y-1.5">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-xl" />
              ))}
            </div>
          ) : tree.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground text-center">
              No categories yet.
            </p>
          ) : (
            <ul className="space-y-0.5">
              {tree.map((cat) => {
                const hasChildren = cat.children.length > 0;
                const isOpen = !!expanded[cat.id];
                const active = isActive(cat);
                const hasActiveChild = cat.children.some(isActive);

                return (
                  <li key={cat.id}>
                    <div
                      className={cn(
                        "group flex items-stretch rounded-xl overflow-hidden transition-all duration-200",
                        (active || hasActiveChild) &&
                          "bg-primary/8 ring-1 ring-primary/15",
                      )}
                    >
                      <Link
                        to={`/category/${encodeURIComponent(cat.slug || cat.name)}`}
                        onClick={() => onOpenChange(false)}
                        className={cn(
                          "flex-1 flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground/85 transition-all duration-200",
                          "hover:bg-muted/70 hover:text-primary hover:translate-x-0.5",
                          active && "text-primary",
                        )}
                      >
                        <span
                          className={cn(
                            "grid h-9 w-9 place-items-center rounded-lg overflow-hidden shrink-0 transition-all duration-200",
                            active
                              ? "bg-primary text-primary-foreground shadow-sm"
                              : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary",
                          )}
                        >
                          {cat.image_url ? (
                            <img
                              src={cat.image_url}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <Tag className="h-4 w-4" />
                          )}
                        </span>
                        <span className="truncate">{cat.name}</span>
                        {active && (
                          <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                        )}
                      </Link>

                      {hasChildren && (
                        <button
                          onClick={() => toggle(cat.id)}
                          aria-label={isOpen ? "Collapse" : "Expand"}
                          aria-expanded={isOpen}
                          className={cn(
                            "shrink-0 px-3 grid place-items-center transition-colors",
                            "hover:bg-muted/70 text-muted-foreground hover:text-primary",
                          )}
                        >
                          <ChevronRight
                            className={cn(
                              "h-4 w-4 transition-transform duration-300 ease-out",
                              isOpen && "rotate-90 text-primary",
                            )}
                          />
                        </button>
                      )}
                    </div>

                    {hasChildren && (
                      <div
                        className={cn(
                          "grid transition-all duration-300 ease-out",
                          isOpen
                            ? "grid-rows-[1fr] opacity-100 mt-0.5"
                            : "grid-rows-[0fr] opacity-0",
                        )}
                      >
                        <ul className="overflow-hidden ml-5 pl-4 border-l border-dashed border-border/70 space-y-0.5 py-0.5">
                          {cat.children.map((sub) => {
                            const subActive = isActive(sub);
                            return (
                              <li key={sub.id}>
                                <Link
                                  to={`/category/${encodeURIComponent(cat.slug || cat.name)}/${encodeURIComponent(sub.slug || sub.name)}`}
                                  onClick={() => onOpenChange(false)}
                                  className={cn(
                                    "group/sub flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-all duration-200",
                                    "hover:bg-muted/70 hover:text-primary hover:translate-x-0.5",
                                    subActive
                                      ? "text-primary font-semibold bg-primary/10"
                                      : "text-foreground/65 font-normal",
                                  )}
                                >
                                  <span
                                    className={cn(
                                      "h-1.5 w-1.5 rounded-full transition-colors",
                                      subActive
                                        ? "bg-primary"
                                        : "bg-muted-foreground/40 group-hover/sub:bg-primary",
                                    )}
                                  />
                                  <span className="truncate">{sub.name}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </nav>

        {/* Footer */}
        <div className="border-t bg-muted/30 px-6 py-3 text-center">
          <p className="text-[11px] text-muted-foreground">
            {tree.length > 0 && `${tree.length} categories available`}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
